import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme as usePrism7Theme } from '../../../contexts/ThemeContext';

interface EmptyWorkflowStateProps {
  /** Manual flow — reveal the canvas + sidebar so the user can pick a step from the palette. */
  onAddFirstStep: () => void;
  /** Business-rules flow — open the existing "Describe a business rule" draft gate. */
  onBuildWithAI: () => void;
  /** NEW — disables only the "Add first step" (manual) card, e.g. until a workflow name has
   * been entered. Defaults to false so every existing caller is unaffected. The "Build with
   * BusinessRules" card is never gated by this — that flow names the workflow as part of its
   * own "Describe a business rule" gate, so it has no name yet at this point either way. */
  addFirstStepDisabled?: boolean;
  /** Tooltip shown on the "Add first step" card while addFirstStepDisabled is true. */
  addFirstStepDisabledReason?: string;
}

/**
 * n8n-style two-card entry screen shown when a workflow has zero nodes. Left card starts the
 * manual flow; right card starts the existing business-rules ("Build with AI") flow. Purely a
 * presentational chooser — it owns no workflow state and calls back into WorkflowCanvasTab,
 * so the underlying canvas/palette/inspector and all API logic stay untouched.
 */
const cardSx = {
  width: 210,
  minHeight: 190,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  cursor: 'pointer',
  border: '2px dashed',
  borderColor: 'divider',
  borderRadius: 2,
  backgroundColor: 'background.paper',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': { borderColor: 'primary.main', boxShadow: 3 },
} as const;

const iconBoxSx = {
  width: 56,
  height: 56,
  mb: 0.5,
  borderRadius: 2,
  border: '2px dashed',
  borderColor: 'primary.main',
  color: 'primary.main',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const EmptyWorkflowState: React.FC<EmptyWorkflowStateProps> = ({
  onAddFirstStep,
  onBuildWithAI,
  addFirstStepDisabled = false,
  addFirstStepDisabledReason,
}) => {
  const { isDarkMode } = usePrism7Theme();

  return (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 600 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <Tooltip title={addFirstStepDisabled ? (addFirstStepDisabledReason ?? '') : ''}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            ...(addFirstStepDisabled
              ? { cursor: 'not-allowed', opacity: 0.5, '&:hover': { borderColor: 'divider', boxShadow: 'none' } }
              : {}),
          }}
          onClick={() => {
            if (!addFirstStepDisabled) onAddFirstStep();
          }}
          role="button"
          tabIndex={0}
          aria-label="Add first step manually"
          aria-disabled={addFirstStepDisabled}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !addFirstStepDisabled) onAddFirstStep();
          }}
        >
          <Box sx={iconBoxSx}>
            <AddIcon />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Add first step…
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center">
            Pick a step manually
          </Typography>
        </Paper>
      </Tooltip>

      <Typography variant="body2" color="text.secondary">
        or
      </Typography>

      <Paper
        elevation={0}
        sx={cardSx}
        onClick={onBuildWithAI}
        role="button"
        tabIndex={0}
        aria-label="Build with BusinessRules from a business rule"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onBuildWithAI();
        }}
      >
        <Box sx={iconBoxSx}>
          <Box
            component="img"
            src={isDarkMode ? '/logo-white.png' : '/logo.png'}
            alt=""
            sx={{ height: 24, width: 'auto', maxWidth: 32, objectFit: 'contain' }}
          />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Build with BusinessRules
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center">
          Describe a business rule
        </Typography>
      </Paper>
    </Box>
  </Box>
  );
};

export default EmptyWorkflowState;
